import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Loader2, ChevronLeft, ChevronRight, X, ImageIcon, Search } from 'lucide-react';
import SEOSchema from '../components/SEOSchema';

const API = '/api';

// CANONICAL URL - Always use production domain
const CANONICAL_DOMAIN = 'https://renaissancegardendubrovnik.com';

// Villa Renaissance Garden brand colors for blur-up placeholders
const BRAND_COLORS = {
  primary: '#4a5d23',      // Olive green
  secondary: '#8b7355',    // Warm taupe  
  accent: '#d4c4a8',       // Sand/cream
  gradient: 'linear-gradient(135deg, #4a5d23 0%, #8b7355 50%, #d4c4a8 100%)'
};

/**
 * Base64 Image Component with Brand-Colored Blur-Up Placeholder
 * 100% Disk Independent with instant perceived loading
 */
const Base64Image = ({ mediaId, alt, onClick, className, testId }) => {
  const [imageData, setImageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  const imgRef = useRef(null);

  // Intersection observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px', threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load Base64 data when in view
  useEffect(() => {
    if (!isInView || !mediaId) return;

    const loadBase64 = async () => {
      try {
        const response = await axios.get(`${API}/media/${mediaId}/base64`, {
          timeout: 30000
        });
        setIsDecoding(true);
        // Small delay to show smooth transition
        setTimeout(() => {
          setImageData(response.data.data_url);
          setIsLoading(false);
          setIsDecoding(false);
        }, 50);
      } catch (error) {
        console.error(`Failed to load image ${mediaId}:`, error);
        setHasError(true);
        setIsLoading(false);
      }
    };

    loadBase64();
  }, [mediaId, isInView]);

  // Brand-colored blur-up placeholder (not in view yet)
  if (!isInView) {
    return (
      <div 
        ref={imgRef}
        className={`min-h-[200px] ${className}`}
        style={{ background: BRAND_COLORS.gradient }}
        data-testid={testId}
      >
        <div className="w-full h-full min-h-[200px] animate-pulse opacity-60" />
      </div>
    );
  }

  // Loading state with brand gradient
  if (isLoading) {
    return (
      <div 
        ref={imgRef}
        className={`flex items-center justify-center min-h-[200px] ${className}`}
        style={{ background: BRAND_COLORS.gradient }}
        data-testid={testId}
      >
        <div className="flex flex-col items-center gap-2 p-8">
          <Loader2 className="h-8 w-8 animate-spin text-white drop-shadow-lg" />
          <span className="text-xs text-white/80 font-medium">Loading...</span>
        </div>
      </div>
    );
  }

  if (hasError || !imageData) {
    return (
      <div 
        ref={imgRef}
        className={`flex items-center justify-center min-h-[200px] ${className}`}
        style={{ background: BRAND_COLORS.accent }}
        data-testid={testId}
      >
        <div className="flex flex-col items-center gap-2 p-8 text-stone-600">
          <ImageIcon className="h-8 w-8" />
          <span className="text-xs">Image unavailable</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={imgRef}
      className={`overflow-hidden ${className}`}
      style={{ background: BRAND_COLORS.gradient }}
      onClick={onClick}
      data-testid={testId}
    >
      <img
        src={imageData}
        alt={alt}
        className={`w-full h-full object-cover transition-all duration-500 ease-out hover:scale-105 ${
          isDecoding ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ animation: 'fadeIn 0.3s ease-out forwards' }}
      />
    </div>
  );
};

/**
 * Lightbox Component with Base64 support and adjacent image preloading
 */
const Lightbox = ({ images, currentIndex, onClose, onNavigate, onImageClick }) => {
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentImageData, setCurrentImageData] = useState(null);
  const [isLoadingImage, setIsLoadingImage] = useState(true);
  const [preloadedImages, setPreloadedImages] = useState({}); // Cache for preloaded images

  const minSwipeDistance = 50;

  // Load current image Base64 and preload adjacent images
  useEffect(() => {
    const currentImage = images[currentIndex];
    if (!currentImage) return;

    // Check if current image is already preloaded
    if (preloadedImages[currentImage.id]) {
      setCurrentImageData(preloadedImages[currentImage.id]);
      setIsLoadingImage(false);
    } else {
      setIsLoadingImage(true);
      axios.get(`${API}/media/${currentImage.id}/base64`, { timeout: 30000 })
        .then(response => {
          const dataUrl = response.data.data_url;
          setCurrentImageData(dataUrl);
          setPreloadedImages(prev => ({ ...prev, [currentImage.id]: dataUrl }));
          setIsLoadingImage(false);
        })
        .catch(() => {
          setCurrentImageData(null);
          setIsLoadingImage(false);
        });
    }

    // Preload adjacent images (prev and next)
    const adjacentIndices = [currentIndex - 1, currentIndex + 1].filter(
      idx => idx >= 0 && idx < images.length
    );
    
    adjacentIndices.forEach(idx => {
      const adjacentImage = images[idx];
      if (adjacentImage && !preloadedImages[adjacentImage.id]) {
        axios.get(`${API}/media/${adjacentImage.id}/base64`, { timeout: 30000 })
          .then(response => {
            setPreloadedImages(prev => ({ 
              ...prev, 
              [adjacentImage.id]: response.data.data_url 
            }));
          })
          .catch(() => {}); // Silent fail for preloading
      }
    });
  }, [currentIndex, images, preloadedImages]);

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && currentIndex < images.length - 1) {
      navigateWithAnimation(currentIndex + 1);
    } else if (isRightSwipe && currentIndex > 0) {
      navigateWithAnimation(currentIndex - 1);
    }
  };

  const navigateWithAnimation = useCallback((newIndex) => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsLoadingImage(true);
    onNavigate(newIndex);
    
    if (images[newIndex]) {
      onImageClick(images[newIndex].id);
    }
    
    setTimeout(() => setIsAnimating(false), 300);
  }, [isAnimating, onNavigate, images, onImageClick]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft' && currentIndex > 0) navigateWithAnimation(currentIndex - 1);
    if (e.key === 'ArrowRight' && currentIndex < images.length - 1) navigateWithAnimation(currentIndex + 1);
  }, [currentIndex, images.length, navigateWithAnimation, onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [handleKeyDown]);

  const currentImage = images[currentIndex];

  return (
    <div 
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="gallery-lightbox"
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-60 p-2 text-white/80 hover:text-white transition-colors bg-black/30 rounded-full hover:bg-black/50"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>

      {/* Image counter */}
      <div className="absolute top-4 left-4 text-white/80 text-sm font-medium bg-black/30 px-3 py-1 rounded-full">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous button (desktop) */}
      {currentIndex > 0 && (
        <button
          onClick={() => navigateWithAnimation(currentIndex - 1)}
          className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white transition-colors bg-black/30 rounded-full hover:bg-black/50"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {/* Next button (desktop) */}
      {currentIndex < images.length - 1 && (
        <button
          onClick={() => navigateWithAnimation(currentIndex + 1)}
          className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 p-3 text-white/80 hover:text-white transition-colors bg-black/30 rounded-full hover:bg-black/50"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Main image with loading state */}
      <div 
        className="relative max-w-[95vw] max-h-[85vh] flex items-center justify-center min-w-[300px] min-h-[300px]"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoadingImage ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 animate-spin text-white" />
            <span className="text-white/60 text-sm">Loading image...</span>
          </div>
        ) : currentImageData ? (
          <img
            src={currentImageData}
            alt={currentImage?.alt_text || currentImage?.title}
            className={`max-w-full max-h-[85vh] object-contain transition-opacity duration-300 ${
              isAnimating ? 'opacity-50' : 'opacity-100'
            }`}
            data-testid="lightbox-image"
          />
        ) : (
          <div className="flex flex-col items-center gap-4 text-white/60">
            <ImageIcon className="h-12 w-12" />
            <span>Image unavailable</span>
          </div>
        )}
      </div>

      {/* Image title */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-center">
        <p className="text-white text-lg font-medium mb-2" data-testid="lightbox-title">
          {currentImage?.alt_text || currentImage?.title}
        </p>
        <p className="text-white/60 text-sm md:hidden">
          Swipe left or right to navigate
        </p>
      </div>

      {/* Thumbnail strip - simplified, no images to avoid loading all */}
      <div className="hidden lg:flex absolute bottom-20 left-1/2 -translate-x-1/2 gap-2 max-w-[80vw] overflow-x-auto py-2">
        {images.map((img, idx) => (
          <button
            key={img.id}
            onClick={() => navigateWithAnimation(idx)}
            className={`flex-shrink-0 w-10 h-10 rounded-full transition-all ${
              idx === currentIndex 
                ? 'bg-white scale-110' 
                : 'bg-white/30 hover:bg-white/50'
            }`}
          >
            <span className="text-xs font-medium text-black/70">{idx + 1}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Main Gallery Page - 100% Disk Independent with Search & Instant Loading
 * Features: Brand-colored placeholders, semantic search, category filtering
 */
const GalleryPage = () => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [images, setImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');

  // Track page visit
  useEffect(() => {
    axios.post(`${API}/stats/track`, { event_type: 'visit', page: 'gallery' }).catch(() => {});
  }, []);

  // Fetch metadata and categories
  useEffect(() => {
    const fetchGalleryData = async () => {
      try {
        const [mediaRes, catRes] = await Promise.all([
          axios.get(`${API}/media`, { timeout: 10000 }),
          axios.get(`${API}/media/categories`, { timeout: 5000 }).catch(() => ({ data: { categories: [] } }))
        ]);
        
        setImages(mediaRes.data || []);
        setFilteredImages(mediaRes.data || []);
        setCategories(catRes.data?.categories || []);
      } catch (error) {
        console.error('Error fetching gallery:', error);
        setImages([]);
        setFilteredImages([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGalleryData();
  }, []);

  // Handle search and filtering
  useEffect(() => {
    let result = [...images];
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(img => 
        img.title?.toLowerCase().includes(query) ||
        img.alt_text?.toLowerCase().includes(query) ||
        img.caption?.toLowerCase().includes(query) ||
        img.category?.toLowerCase().includes(query)
      );
    }
    
    if (selectedCategory) {
      result = result.filter(img => 
        img.category?.toLowerCase() === selectedCategory.toLowerCase()
      );
    }
    
    setFilteredImages(result);
  }, [searchQuery, selectedCategory, images]);

  const handleImageClick = (index) => {
    // Find the actual index in full images array
    const clickedImage = filteredImages[index];
    const actualIndex = images.findIndex(img => img.id === clickedImage.id);
    setSelectedIndex(actualIndex >= 0 ? actualIndex : index);
    
    if (clickedImage) {
      axios.post(`${API}/stats/track`, { 
        event_type: 'image_click', 
        image_id: clickedImage.id 
      }).catch(() => {});
    }
  };

  const trackImageView = (imageId) => {
    axios.post(`${API}/stats/track`, { 
      event_type: 'image_click', 
      image_id: imageId 
    }).catch(() => {});
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
  };

  if (isLoading) {
    return (
      <div className="pt-20 stone-texture min-h-screen flex items-center justify-center" data-testid="gallery-loading">
        <SEOSchema page="gallery" />
        <div className="flex flex-col items-center gap-4" style={{ color: BRAND_COLORS.primary }}>
          <Loader2 className="h-12 w-12 animate-spin" />
          <span className="text-muted-foreground">Loading gallery...</span>
        </div>
      </div>
    );
  }

  if (images.length === 0) {
    return (
      <div className="pt-20 stone-texture min-h-screen flex items-center justify-center" data-testid="gallery-empty">
        <SEOSchema page="gallery" />
        <div className="flex flex-col items-center gap-4 text-muted-foreground">
          <ImageIcon className="h-16 w-16" />
          <span>No images available</span>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 stone-texture min-h-screen" data-testid="gallery-page">
      {/* Page-specific SEO via SEOSchema component */}
      <SEOSchema page="gallery" />

      {/* CSS for fade-in animation */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(1.02); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <h1 className="font-playfair text-4xl sm:text-5xl font-bold text-center mb-6" data-testid="gallery-title">
          Photo Gallery
        </h1>
        <p className="text-center text-muted-foreground text-lg mb-8 max-w-2xl mx-auto" data-testid="gallery-subtitle">
          Explore the beauty of Villa Renaissance Garden - from the historic 1000 m² garden to the luxurious pool area
        </p>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-4 mb-12 max-w-2xl mx-auto" data-testid="gallery-search">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search images... (pool, garden, bedroom)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white"
              data-testid="search-input"
            />
          </div>
          
          {categories.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-3 border border-stone-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/50 bg-white min-w-[150px]"
              data-testid="category-filter"
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          )}
          
          {(searchQuery || selectedCategory) && (
            <button
              onClick={clearFilters}
              className="px-4 py-3 text-sm text-muted-foreground hover:text-primary transition-colors"
              data-testid="clear-filters"
            >
              Clear
            </button>
          )}
        </div>

        {/* Results count */}
        {(searchQuery || selectedCategory) && (
          <p className="text-center text-muted-foreground mb-8" data-testid="results-count">
            Showing {filteredImages.length} of {images.length} images
            {searchQuery && ` matching "${searchQuery}"`}
            {selectedCategory && ` in ${selectedCategory}`}
          </p>
        )}

        {/* Image Grid */}
        {filteredImages.length > 0 ? (
          <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4" data-testid="gallery-grid">
            {filteredImages.map((image, index) => (
              <Base64Image
                key={image.id}
                mediaId={image.id}
                alt={image.alt_text || image.caption || image.title}
                onClick={() => handleImageClick(index)}
                className="break-inside-avoid cursor-pointer rounded-none shadow-md hover:shadow-xl transition-all duration-300 relative"
                testId={`gallery-image-${index}`}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No images match your search</p>
            <button
              onClick={clearFilters}
              className="mt-4 text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>

      {selectedIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={selectedIndex}
          onClose={() => setSelectedIndex(null)}
          onNavigate={setSelectedIndex}
          onImageClick={trackImageView}
        />
      )}
    </div>
  );
};

export default GalleryPage;
